import React from 'react';
import I18n from 'i18n-js';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import URLHelpers from '../shared/url_helpers';
import { RCButton } from '@reverbdotcom/cadence/components';

export const NO_LISTINGS = 'noListings';
export const NO_LISTINGS_MATCHING = 'noListingsMatching';
export const NO_LISTINGS_ON_PAGE = 'noListingsOnPage';

const COMPONENT_NAME = 'EmptyListingsState';

interface IExternalProps {
  message?: string;
}

export function clearAllListingsManagementFilters({ hasResults }: { hasResults: boolean }) {
  const url = new URL(window.location.href);
  const { searchParams } = url;
  const paramsSize = Array.from(searchParams).length;
  const state = searchParams.get('state');

  const hasMultipleParams = paramsSize > 1;
  const hasStateParam = !!state;

  // In the UI filters, we always force a state now.
  // A URL with no state param gets redirected by the controller to a default state with results.
  // Keep the state param when other filters are also present, or when that state has results of its own.
  // Drop the state param when no other filters are present and that state has no results, so the controller picks a state with results.
  const canPreserveState = hasStateParam && (hasMultipleParams || hasResults);

  const clearFilterUrl = canPreserveState ?
    URLHelpers.listingsManagerPath({ state }) :
    URLHelpers.listingsManagerPath();

  // Already on the cleared URL, so there is nothing to clear.
  if (clearFilterUrl === `${url.pathname}${url.search}`) return;

  window.location.assign(clearFilterUrl);
}

export default function EmptyListingsState({ message = '' }: IExternalProps) {
  const messageKey = message || NO_LISTINGS_ON_PAGE;

  return (
    <div className="d-flex fx-dir-col fx-align-center ptb-6 mt-20 mobile-mt-0">
      <div className="weight-bold size-120">
        {I18n.t(`discovery.listingsManagement.emptyState.${messageKey}`)}
      </div>

      {messageKey === NO_LISTINGS && (
        <CoreLink
          className="seller-listing-cards-collection__empty__create-listing-link"
          to={URLHelpers.sellFormSearchPath()}
          target="_blank"
          clickEvent={{
            eventName: MParticleEventName.ClickedLinkToSellFormSearch,
            componentName: COMPONENT_NAME,
          }}
        >
          {I18n.t('discovery.listingsManagement.emptyState.createNewListing')}
        </CoreLink>
      )}

      {messageKey === NO_LISTINGS_MATCHING && (
        <div className="mt-5">
          <RCButton
            variant="outlined"
            onClick={() => {
              trackEvent({
                eventName: MParticleEventName.ClickedClearFiltersButton,
                componentName: COMPONENT_NAME,
              });

              clearAllListingsManagementFilters({
                hasResults: false,
              });
            }}
          >
            {I18n.t('discovery.listingsManagement.emptyState.clearFilters')}
          </RCButton>
        </div>
      )}
    </div>
  );
}
